import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Modal, Card, Table, Button, Row, Col, ListGroup, Form, Badge } from 'react-bootstrap';
import { VehiclesClient } from '../web-api-client.ts';
import Barcode from 'react-barcode';
import { convertUtcToLocal, isMobileDevice } from './utils/utils.js';
import { ButtonGroup } from 'reactstrap';
import Upload from './Upload.js';
import BackButton from './BackButton.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import UserContext from '../providers/UserContext.js';
import { debounce } from 'lodash';
import ScannerLauncher from './ScannerLauncher.js';
import Loader from './Loader.js';
import useToast from '../hooks/UseToast.js';
import AsyncSelect from 'react-select/async';

const VehicleDetails = () => {
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);

  const [showDeleteVehicleModal, setShowDeleteVehicleModal] = useState(false);
  const [vehicleHistory, setVehicleHistory] = useState([]);
  const [originalVehicle, setOriginalVehicle] = useState(null);

  const [selectedMake, setSelectedMake] = useState(null);   // { value, label }
  const [selectedModel, setSelectedModel] = useState(null); // { value, label }

  const user = useContext(UserContext);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();

  // Technicians cannot see or modify price. Hide the row entirely rather
  // than disabling — keeps the form layout cleaner and avoids hinting at
  // the existence of a value they can't access. Server enforces too.
  const canSeePrice = !(user?.roles && user.roles.includes('Technician'));

  useEffect(() => {
    populateData();
  }, [id]);

  const populateData = async () => {
    setLoading(true);
    try {
      await loadVehicle();
    } finally {
      setLoading(false);
    }
  };

  const loadVehicle = async () => {
    const client = new VehiclesClient();
    const data = await client.getVehicleById(id);
    setVehicle(data);
    setOriginalVehicle(data);

    // initialize selected make/model for AsyncSelects (if present)
    setSelectedMake(
      data?.makeId ? { value: data.makeId, label: data.makeName ?? String(data.makeId) } : null
    );
    setSelectedModel(
      data?.modelId ? { value: data.modelId, label: data.modelName ?? String(data.modelId) } : null
    );
  };

  const getVehicleStatus = (statusId) => {
    if (statusId === 1 || statusId === '1') return 'Active';
    if (statusId === 2 || statusId === '2') return 'Draft';
    return 'Unknown';
  };

  const saveChanges = async (veh) => {
    setLoading(true);
    try {
      const client = new VehiclesClient();
      const data = await client.updateVehicle(veh.id, { ...veh });
      setVehicle(data);
      setOriginalVehicle(data);
      setEditMode(false);
      showToast('Vehicle updated successfully!', 'success');
    } finally {
      setLoading(false);
    }
  };

  const discardChanges = async () => {
    await loadVehicle();
    setEditMode(false);
  };

  const handleDeleteVehicleClick = () => setShowDeleteVehicleModal(true);

  const handleDeleteVehicleConfirm = async () => {
    try {
      const client = new VehiclesClient();
      await client.deleteVehicle(id);
      setShowDeleteVehicleModal(false);
      navigate('/vehicles');
    } catch (error) {
      console.error('Error deleting vehicle:', error);
    }
  };

  const handleVinChange = (barcode) => {
    if (barcode) setVehicle((v) => ({ ...v, vin: barcode }));
  };

  const toOption = (x) => ({ value: x.id, label: x.label });

  /** -------------------- Async loaders (like NewVehicle) -------------------- **/
  const fetchMakes = useCallback(async (inputValue) => {
    const client = new VehiclesClient();
    const term = inputValue && inputValue.trim() ? inputValue.trim() : undefined;
    const data = await client.getMakes(term);
    return data.map(toOption);
  }, []);

  const debouncedFetchMakes = useMemo(
    () =>
      debounce((input, resolve, reject) => {
        fetchMakes(input).then(resolve).catch(reject);
      }, 300),
    [fetchMakes]
  );

  const loadMakes = useCallback(
    (inputValue) =>
      new Promise((resolve, reject) => {
        debouncedFetchMakes(inputValue, resolve, reject);
      }),
    [debouncedFetchMakes]
  );

  const fetchModels = useCallback(async (makeId, inputValue) => {
    if (!makeId) return [];
    const client = new VehiclesClient();
    const term = inputValue && inputValue.trim() ? inputValue.trim() : undefined;
    const data = await client.getModelsByMake(makeId, term);
    return data.map(toOption);
  }, []);

  const debouncedFetchModels = useMemo(
    () =>
      debounce((makeId, input, resolve, reject) => {
        fetchModels(makeId, input).then(resolve).catch(reject);
      }, 300),
    [fetchModels]
  );

  const loadModels = useCallback(
    (inputValue) =>
      new Promise((resolve, reject) => {
        debouncedFetchModels(vehicle?.makeId, inputValue, resolve, reject);
      }),
    [debouncedFetchModels, vehicle?.makeId]
  );


  const formatMiles = (price) => {
    let num = Number(price)
    if (num < 1000) {
      return (num.toString())
    }
    if ((num / 1000) < 1000) {
      return (Math.floor(num / 1000).toString().concat(",") + ((num % 1000).toString().padStart(3, '0')))
    }
    else {
      return (formatMiles(Math.floor(num / 1000).toString()).concat(",") + ((num % 1000).toString().padStart(3, '0')))
    }
  }




  /** Cleanup debouncers on unmount */
  useEffect(() => {
    return () => {
      debouncedFetchMakes.cancel();
      debouncedFetchModels.cancel();
    };
  }, [debouncedFetchMakes, debouncedFetchModels]);

  if (loading && !vehicle) return <Loader />;

  return (
    <div>
      {vehicle && (
        <div className="container mt-4 mb-5">
          <BackButton />

          <Row>
            <Col md={12}>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h4>
                    {vehicle?.vin || ''}
                    {/*  {' '}
                    {editMode ? (
                      <select
                        className="form-select d-inline w-auto ms-2 mt-2"
                        value={vehicle.status}
                        onChange={(e) =>
                          setVehicle((v) => ({ ...v, status: e.target.value }))
                        }
                      >
                        <option value="1">Active</option>
                        <option value="2">Draft</option>
                      </select>
                    ) : (
                      <span className="badge bg-success">{getVehicleStatus(vehicle.status)}</span>
                    )} */}
                  </h4>

                  {editMode ? (
                    <div className="text-end">
                      <Button
                        variant="success"
                        className="me-2 mt-2"
                        onClick={() => saveChanges(vehicle)}
                      >
                        Save
                      </Button>
                      <Button
                        variant="secondary"
                        className="me-2 mt-2"
                        onClick={discardChanges}
                      >
                        Discard
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <ButtonGroup>
                        <Button className="me-2" onClick={() => setEditMode(true)}>
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          className="me-2"
                          onClick={handleDeleteVehicleClick}
                        >
                          Delete Vehicle
                        </Button>
                      </ButtonGroup>
                    </div>
                  )}
                </Card.Header>

                <Card.Body>
                  <Row>
                    <Col md={9}>
                      <Table striped bordered>
                        <tbody>
                          <tr>
                            <td>Year</td>
                            <td>
                              {editMode ? (
                                <select
                                  className="form-select"
                                  value={vehicle.year ?? ''}
                                  onChange={(e) =>
                                    setVehicle((v) => ({ ...v, year: e.target.value }))
                                  }
                                >
                                  {Array.from({ length: 40 }, (_, i) => 2026 - i).map((y) => (
                                    <option key={y} value={y}>
                                      {y}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                vehicle.year
                              )}
                            </td>
                          </tr>

                          <tr>
                            <td>Make</td>
                            <td>
                              {editMode ? (
                                <AsyncSelect
                                  classNamePrefix="rs"
                                  className="react-select-container"
                                  cacheOptions
                                  defaultOptions
                                  loadOptions={loadMakes}
                                  value={selectedMake}
                                  onChange={(opt) => {
                                    setSelectedMake(opt);
                                    setSelectedModel(null);
                                    setVehicle((v) => ({
                                      ...v,
                                      makeId: opt?.value ?? null,
                                      makeName: opt?.label ?? '',
                                      modelId: null,       // clear dependent model
                                      modelName: '',       // clear display label too
                                    }));
                                  }}
                                  placeholder="Search make…"
                                  isClearable
                                  noOptionsMessage={() => 'Type to search makes'}
                                  onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                                />
                              ) : (
                                vehicle.makeName
                              )}
                            </td>
                          </tr>

                          <tr>
                            <td>Model</td>
                            <td>
                              {editMode ? (
                                <AsyncSelect
                                  key={vehicle.makeId ?? 'no-make'}
                                  classNamePrefix="rs"
                                  className="react-select-container"
                                  cacheOptions
                                  defaultOptions={!!vehicle.makeId}
                                  loadOptions={loadModels}
                                  value={selectedModel}
                                  onChange={(opt) => {
                                    setSelectedModel(opt);
                                    setVehicle((v) => ({
                                      ...v,
                                      modelId: opt?.value ?? null,
                                      modelName: opt?.label ?? '',
                                    }));
                                  }}
                                  placeholder={vehicle.makeId ? 'Search model…' : 'Select a make first'}
                                  isDisabled={!vehicle.makeId}
                                  isClearable
                                  noOptionsMessage={() =>
                                    vehicle.makeId ? 'Type to search models' : 'Select a make'
                                  }
                                  onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                                />
                              ) : (
                                vehicle.modelName
                              )}
                            </td>
                          </tr>

                          <tr>
                            <td>Stock Number</td>
                            <td>
                              {editMode ? (
                                <input
                                  type="text"
                                  className="form-control"
                                  value={vehicle.stockNumber ?? ''}
                                  onChange={(e) =>
                                    setVehicle((v) => ({ ...v, stockNumber: e.target.value }))
                                  }
                                />
                              ) : (
                                vehicle.stockNumber
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td>Trim</td>
                            <td>
                              {editMode ? (
                                <input
                                  type="text"
                                  className="form-control"
                                  value={vehicle.trim ?? ''}
                                  onChange={(e) =>
                                    setVehicle((v) => ({ ...v, trim: e.target.value }))
                                  }
                                />
                              ) : (
                                vehicle.trim
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td>Color</td>
                            <td>
                              {editMode ? (
                                <input
                                  type="text"
                                  className="form-control"
                                  value={vehicle.color ?? ''}
                                  onChange={(e) =>
                                    setVehicle((v) => ({ ...v, color: e.target.value }))
                                  }
                                />
                              ) : (
                                vehicle.color
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td>Mileage</td>
                            <td>
                              {editMode ? (
                                <input
                                  type="number"
                                  className="form-control"
                                  value={vehicle.mileage ?? ''}
                                  onChange={(e) =>
                                    setVehicle((v) => ({ ...v, mileage: e.target.value }))
                                  }
                                />
                              ) : (
                                (vehicle.mileage != null) ? (formatMiles(vehicle?.mileage)) : ''
                              )}
                            </td>
                          </tr>
                          {canSeePrice && (
                            <tr>
                              <td>Price</td>
                              <td>
                                {editMode ? (
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={vehicle.price ?? ''}
                                    onChange={(e) =>
                                      setVehicle((v) => ({ ...v, price: e.target.value === '' ? null : Number(e.target.value) }))
                                    }
                                  />
                                ) : (
                                  (vehicle.price != null) ? `$${(vehicle?.price).toLocaleString('en-US')}` : ''
                                )}
                              </td>
                            </tr>
                          )}

                          <tr>
                            <td>Condition</td>
                            <td>
                              {editMode ? (
                                <select
                                  className="form-select"
                                  value={vehicle.condition ?? ''}
                                  onChange={(e) =>
                                    setVehicle((v) => ({ ...v, condition: e.target.value === '' ? null : Number(e.target.value) }))
                                  }
                                >
                                  <option value="">-- Select --</option>
                                  <option value={0}>New</option>
                                  <option value={1}>Used</option>
                                </select>
                              ) : (
                                vehicle.condition === 0 ? 'New' : vehicle.condition === 1 ? 'Used' : ''
                              )}
                            </td>
                          </tr>


                          {editMode ? (
                            <>
                              <tr>
                                <td>Vin</td>
                                <td>
                                  <div className="input-group">
                                    <input
                                      type="text"
                                      className="form-control"
                                      name="vin"
                                      value={vehicle.vin ?? ''}
                                      onChange={(e) =>
                                        setVehicle((v) => ({ ...v, vin: e.target.value }))
                                      }
                                    />
                                    {isMobileDevice() && (
                                      <ScannerLauncher
                                        onBarcodeDetected={handleVinChange}
                                        mode="button"
                                        buttonClasses="ms-2"
                                      />
                                    )}
                                  </div>
                                </td>
                              </tr>
                            </>
                          ) : (
                            <>
                              <tr>
                                <td>Vin</td>
                                <td>{vehicle.vin}</td>
                              </tr>
                            </>
                          )}
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={3}>

                      {/* Product Image */}
                      {vehicle.photoUri && <Card.Img
                        src={vehicle.photoUri}
                        alt="Server Image"
                        className="img-fluid mb-2"
                        style={{ maxHeight: "200px", maxWidth: "100%", objectFit: "contain" }}
                      />}

                      <Upload type="vehicle" entityId={vehicle.id} buttonLabel={vehicle.photoUri ? "Replace Image" : "Upload an Image"} onComplete={loadVehicle} />




                    </Col>

                    {/* Right column with image/barcode can be restored here if needed */}
                  </Row>
                </Card.Body>
              </Card>
            </Col>

          </Row>

          {/* Vehicle History (optional) */}
        </div>
      )}

      <Modal
        show={showDeleteVehicleModal}
        onHide={() => setShowDeleteVehicleModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete Vehicle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this vehicle? All inspection data for this vehicle will be deleted. This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteVehicleModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteVehicleConfirm}>
            Delete Vehicle
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VehicleDetails;
